import { useEffect, useRef } from 'react';

import { useGetClustersQuery } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/query';
import { clustersSelector } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/selectors';
import { useMapConfigurationBboxSelector } from '@/packages/redux-state/store/slices/mapConfiguration/selectors/smart';
import { useActivePinIdSelector } from '@/packages/redux-state/store/slices/mapControls';

import { useUpdateChangedClusters } from '../../../../hooks/useUpdateChangedClusters';

import { useGetClusterByActivePinId } from './useGetClusterByActivePinId';

/**
 * Хук следит за изменением активного пина. Примеры:
 * 1) был выбран пин, нажали на другой пин
 * 2) был выбран пин, закрыли листинг по пину
 * 3) был выбран пин, закрыли листинг по пину, нажали на другой пин
 * Его задача - выбрать текущий активный кластер, предыдущий активный кластер,
 * поменять их поля и отдать команду на обновление слоёв и менеджеров.
 */
export function useTrackActivePinChanged(): void {
  const activePinId = useActivePinIdSelector();
  const { data: clusters } = useGetClustersQuery({ select: clustersSelector });
  const activeCluster = useGetClusterByActivePinId();
  const updateChangedClusters = useUpdateChangedClusters();
  const mapBbox = useMapConfigurationBboxSelector();

  const previousActivePinId = useRef<string>();

  useEffect(() => {
    const deactivatedCluster = clusters?.find(item => item.id === previousActivePinId.current);

    if (deactivatedCluster) {
      delete deactivatedCluster.properties?.active;
    }

    if (activeCluster) {
      activeCluster.properties ??= {};
      activeCluster.properties.active = 1;
      delete activeCluster.properties.hovered;
    }

    updateChangedClusters(activeCluster, deactivatedCluster);

    previousActivePinId.current = activePinId;
  }, [activePinId, activeCluster, clusters, updateChangedClusters, previousActivePinId, mapBbox]);
}
